import React from "react";
import { useLocation } from "react-router-dom";
import {
  User,
  Globe,
  Menu,
  LogOut,
  Mail,
  Users,
  Sparkles,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { openAppOrWeb } from "@/lib/openAppOrWeb";
import { openAnnouncement } from "@/lib/announcement";
import { navigationItems } from "./navigation";

interface PortalHeaderProps {
  onToggleSidebar: () => void;
}

const iconButton =
  "h-10 rounded-full px-3 text-muted-foreground hover:text-foreground hover:bg-muted";

export const PortalHeader: React.FC<PortalHeaderProps> = ({
  onToggleSidebar,
}) => {
  const { language, setLanguage, t } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleLanguageToggle = () => {
    setLanguage(language === "ar" ? "en" : "ar");
  };

  return (
    <header className="portal-header">
      <div className="portal-header-bar">
        {/* Brand */}
        <div className="flex items-center gap-1 sm:gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleSidebar}
            className="h-10 w-10 p-0 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <a href="/" className="flex items-center gap-2 min-w-0">
            <img
              src="/uploads/a0faf86a-8d67-40e8-8452-60261fdb6d9b.png"
              alt="WSM Logo"
              className="w-16 h-14 shrink-0 object-contain"
            />
            <span className="hidden sm:block lg:hidden truncate text-base font-bold">
              {t("portal.title")}
            </span>
          </a>
        </div>

        {/* Primary navigation (desktop) */}
        <nav className="hidden lg:flex items-center gap-0.5 rounded-full bg-muted p-1">
          {navigationItems.map((item) => {
            const active = location.pathname === item.href;
            return (
              <a
                key={item.key}
                href={item.href}
                className={cn(
                  "whitespace-nowrap rounded-full px-3 xl:px-4 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-ink text-white shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t(item.key)}
              </a>
            );
          })}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          {/* Outlook */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              openAppOrWeb("ms-outlook://", "https://outlook.office.com/mail/")
            }
            className={cn(iconButton, "hidden md:inline-flex")}
            title={t("nav.outlook")}
          >
            <Mail className="h-5 w-5" />
            <span className="text-sm font-medium hidden xl:inline">
              {t("nav.outlook")}
            </span>
          </Button>

          {/* Teams */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              openAppOrWeb("msteams:", "https://teams.microsoft.com/")
            }
            className={cn(iconButton, "hidden md:inline-flex")}
            title={t("nav.teams")}
          >
            <Users className="h-5 w-5" />
            <span className="text-sm font-medium hidden xl:inline">
              {t("nav.teams")}
            </span>
          </Button>

          {/* Wasmi (company AI chat) - not launched yet */}
          <Button
            variant="ghost"
            size="sm"
            onClick={openAnnouncement}
            className={cn(iconButton, "hidden md:inline-flex")}
            title={`${t("nav.wasmi")} - ${t("common.coming_soon")}`}
          >
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium hidden xl:inline">
              {t("nav.wasmi")}
            </span>
            <Badge
              variant="secondary"
              className="px-1.5 py-0 text-[10px] font-normal hidden xl:inline-flex"
            >
              {t("common.coming_soon")}
            </Badge>
          </Button>

          <span className="mx-1 hidden md:block h-6 w-px bg-border" />

          {/* Language Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLanguageToggle}
            className={iconButton}
          >
            <Globe className="h-5 w-5" />
            <span className="text-sm font-medium hidden sm:inline">
              {language === "ar" ? "العربية" : "EN"}
            </span>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-10 w-10 p-0">
                <div className="w-9 h-9 bg-ink rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-2xl p-1">
              <div className="p-3">
                <p className="font-medium">
                  {user?.displayName || user?.fullName || "المستخدم"}
                </p>
                <p className="text-sm text-muted-foreground break-all">
                  {user?.email || user?.username || "Office 365"}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive rounded-xl gap-2"
                onClick={logout}
              >
                <LogOut className="h-4 w-4" />
                {t("common.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
